import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, type Child, type TreeState } from '../api'
import { radius, theme } from '../theme'
import { Card, Page } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Trace } from '../lib/trace'
import { sfx } from '../lib/sound'
import { BackRow } from './MysteryWord'
import { SproutBuddy, DEFAULT_BUDDY, parseBuddy } from '../components/SproutBuddy'
import { WordField } from '../components/WordField'
import { ForestScene } from '../components/ForestScene'

/**
 * My tree: a sprout that grows into a giant sequoia.
 *
 * Sam's idea, 5 Oct 2026: the app is called Word Sprout, so let the sprout
 * become a tree. It reads the one number that cannot be rushed — words fully
 * grown, which takes right answers on several separate days — so the tree is
 * honest about learning in a way a points total is not. Games do not grow it
 * (D-063), and like the garden it never shrinks (D-048).
 *
 * Twenty-five stages: eleven from a seed to a giant sequoia at 1,200 grown
 * words, then fourteen in which its seeds grow a grove and then a forest, to
 * the Giant Forest at 10,000 (22 Oct 2026; Sam: more tiers to stay
 * motivated, and one kind of tree rather than many, to avoid confusion). The
 * names, word counts and the sequoia facts all come from the server
 * (repo/games.py), so they live in one place and are tested there.
 *
 * Drawn, not photographed: a few shapes whose sizes follow the stage. A
 * young sequoia is a narrow cone of branches; an old one is a tall bare red
 * trunk with clumps of green high up, and that change is what the drawing
 * follows.
 */

// Held in a constant because tests/test_trace_labels.py reads every literal
// id= in the front end as a provenance label, and this one is not.
const SKY = 'ws-tree-sky'
const HEIGHT = [0, 0, 0, 92, 128, 166, 204, 240, 272, 300, 326]
const TRUNK = [0, 0, 0, 7, 10, 13, 17, 22, 27, 32, 38]

function seeded(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

export function TreeDrawing({ stage }: { stage: number }) {
  const ground = 372
  const cx = 160
  const leaf = '#3F7D3A'
  const leafDark = '#2F6230'
  const leafLight = '#5FA052'
  const bark = '#9C4A2B'
  const barkDark = '#6E301B'

  const parts = useMemo(() => {
    const out: ReactNode[] = []
    if (stage === 0) {
      out.push(
        <ellipse key="seed" cx={cx} cy={ground - 5} rx={13} ry={7} fill="#8B5A2B" />,
        <path key="spark" d={`M${cx + 16} ${ground - 26} l3 -8 l3 8 l8 3 l-8 3 l-3 8 l-3 -8 l-8 -3 z`}
          fill="#FFD25A" className="ws-twinkle" />,
      )
      return out
    }
    if (stage === 1 || stage === 2) {
      const h = stage === 1 ? 40 : 70
      out.push(<path key="stem" d={`M${cx} ${ground} C ${cx - 2} ${ground - h / 2}, ${cx + 2} ${ground - h / 2}, ${cx} ${ground - h}`}
        stroke="#4E8B3A" strokeWidth={4} fill="none" strokeLinecap="round" />)
      const pairs = stage === 1 ? 1 : 3
      for (let i = 0; i < pairs; i++) {
        const y = ground - h + i * 18
        const s = (stage === 1 ? 1.5 : 1.3) - i * 0.15
        out.push(
          <ellipse key={`l${i}`} cx={cx - 11 * s} cy={y} rx={11 * s} ry={6 * s} fill={leafLight}
            transform={`rotate(-25 ${cx - 11 * s} ${y})`} />,
          <ellipse key={`r${i}`} cx={cx + 11 * s} cy={y} rx={11 * s} ry={6 * s} fill={leaf}
            transform={`rotate(25 ${cx + 11 * s} ${y})`} />,
        )
      }
      return out
    }

    // A sequoia. Young: branches nearly to the ground, a neat cone. Old: the
    // lower trunk bare, the crown a crowd of rounded clumps.
    // Stages past the giant sequoia are whole scenes (ForestScene), so this
    // draws only the first eleven; `main` keeps it in range for them.
    const main = Math.min(stage, HEIGHT.length - 1)
    const H = HEIGHT[main]!
    const W = TRUNK[main]!
    const old = main >= 6
    const rnd = seeded(main * 97 + 13)

    // Flared base on the old ones, the way a real giant stands.
    const flare = old ? W * 0.5 : 0
    out.push(
      <path key="trunk"
        d={`M${cx - W / 2 - flare} ${ground} Q ${cx - W / 2} ${ground - 18}, ${cx - W / 2} ${ground - 30}
            L ${cx - W * 0.14} ${ground - H * 0.86} L ${cx + W * 0.14} ${ground - H * 0.86}
            L ${cx + W / 2} ${ground - 30} Q ${cx + W / 2} ${ground - 18}, ${cx + W / 2 + flare} ${ground} Z`}
        fill={bark} />,
    )
    if (old) {
      // The deep furrows in the bark.
      for (let i = -2; i <= 2; i++) {
        const x = cx + i * W * 0.17
        out.push(<path key={`f${i}`} d={`M${x} ${ground - 2} L ${x + i * 0.6} ${ground - H * 0.5}`}
          stroke={barkDark} strokeWidth={1.4} opacity={0.6} />)
      }
    }

    if (!old) {
      // Young: a dense cone of branches nearly to the ground, in overlapping
      // tiers, each a triangle with a slightly drooping lower edge.
      const tiers = 3 + stage
      for (let i = 0; i < tiers; i++) {
        const f = i / tiers
        const yb = ground - H * (0.07 + 0.8 * f)
        const ya = yb - H * 0.3
        const hw = H * 0.3 * (1 - f * 0.82)
        out.push(
          <path key={`t${i}`}
            d={`M${cx - hw} ${yb} Q ${cx} ${yb + 7}, ${cx + hw} ${yb} L ${cx} ${ya} Z`}
            fill={i % 2 ? leafDark : leaf} />,
          <path key={`th${i}`}
            d={`M${cx - hw * 0.55} ${yb - 3} L ${cx} ${ya + 4} L ${cx - hw * 0.1} ${yb - 2} Z`}
            fill={leafLight} opacity={0.5} />,
        )
      }
      return out
    }

    // Old: the trunk bare for nearly half its height, then a narrow, lumpy
    // crown of rounded clumps. A giant sequoia is about three times as tall
    // as it is wide.
    //
    // Until 18 Oct 2026 the clumps stood alone with sky between them, and in
    // the rows of two the bare trunk showed up the middle of the crown. Sam:
    // "you can see empty space and the tree trunk; strange". Now a solid
    // crown in the clumps' own outline sits behind them, so the edge stays
    // lumpy and the inside is all leaves.
    const crownFrom = 0.44
    const rows = 6 + main
    const leaves = [leaf, leafDark, '#4A8A40']
    const branches: ReactNode[] = []
    const clumps: ReactNode[] = []
    const outline: [number, number][] = []
    for (let i = 0; i < rows; i++) {
      const f = i / (rows - 1)
      const y = ground - H * (crownFrom + (1 - crownFrom) * f)
      // Widest a third of the way up the crown, round at the top.
      const half = H * 0.16 * (0.62 + 0.45 * Math.sin(Math.PI * Math.min(1, f * 1.25 + 0.15))) * (1 - 0.3 * f)
      const per = f > 0.85 ? 1 : f > 0.6 ? 2 : 3
      // Narrowing to a rounded point at the very top, not a flat cap.
      outline.push([y, half * 0.9 * Math.min(1, 0.3 + (1 - f) * 4)])
      for (let k = 0; k < per; k++) {
        const across = per === 1 ? 0 : (k / (per - 1)) * 2 - 1
        const x = cx + across * half * (per === 2 ? 0.5 : 0.72) + (rnd() - 0.5) * 8
        const rx = (per === 1 ? half * 0.9 : half * 0.48) + rnd() * 5
        const ry = rx * (0.58 + rnd() * 0.12)
        const yy = y + (rnd() - 0.5) * 6
        if (per === 3 && k !== 1 && i < rows * 0.5) {
          branches.push(<path key={`b${i}${k}`} d={`M${cx} ${yy + 6} L ${x} ${yy + 2}`}
            stroke={barkDark} strokeWidth={2.2} strokeLinecap="round" />)
        }
        clumps.push(
          <ellipse key={`c${i}${k}`} cx={x} cy={yy} rx={rx} ry={ry}
            fill={leaves[Math.floor(rnd() * leaves.length)]} />,
          <ellipse key={`h${i}${k}`} cx={x - rx * 0.28} cy={yy - ry * 0.38} rx={rx * 0.45} ry={ry * 0.38}
            fill={leafLight} opacity={0.5} />,
        )
      }
    }
    // Up the left side, over the top, down the right.
    const up = outline.map(([y, h]) => `${(cx - h).toFixed(1)} ${y.toFixed(1)}`)
    const down = [...outline].reverse().map(([y, h]) => `${(cx + h).toFixed(1)} ${y.toFixed(1)}`)
    const top = ground - H - H * 0.05
    out.push(<path key="crown"
      d={`M ${up.join(' L ')} Q ${cx} ${top}, ${down.join(' L ')} Z`}
      fill={leafDark} stroke={leafDark} strokeWidth={14} strokeLinejoin="round" />)
    out.push(...branches, ...clumps)
    // A little bird, from the big tree on: a tree that size has visitors.
    const by = ground - H * 0.5
    out.push(<path key="bird" d={`M${cx + H * 0.2} ${by} q 5 -6 10 0 q 5 -6 10 0`}
      stroke="#2E2140" strokeWidth={2} fill="none" strokeLinecap="round" />)
    return out
  }, [stage])

  // From a tiny grove on, the camera pulls back over a whole forest.
  if (stage >= HEIGHT.length) return <ForestScene g={stage - (HEIGHT.length - 1)} />

  return (
    <svg viewBox="0 0 320 400" role="img" aria-label={`A tree at stage ${stage + 1}`}
      style={{ width: '100%', maxWidth: 420, display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id={SKY} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#CDEBFF" />
          <stop offset="1" stopColor="#FFF7E6" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height="400" rx="18" fill={`url(#${SKY})`} />
      <circle cx="268" cy="56" r="22" fill="#FFD25A" opacity={0.9} />
      {stage >= 8 ? (
        // Tall enough to reach the clouds.
        <g fill="#FFFFFF" opacity={0.92}>
          <ellipse cx="72" cy="70" rx="34" ry="12" />
          <ellipse cx="96" cy="62" rx="22" ry="12" />
          <ellipse cx="226" cy="108" rx="30" ry="10" />
        </g>
      ) : null}
      <ellipse cx="160" cy="392" rx="210" ry="34" fill="#9CCB6B" />
      <ellipse cx="160" cy="388" rx="190" ry="24" fill="#B5DB84" />
      <g className="ws-grow" style={{ transformOrigin: '160px 372px' }}>{parts}</g>
    </svg>
  )
}

let styled = false
function ensureStyle(): void {
  if (styled || typeof document === 'undefined') return
  styled = true
  const el = document.createElement('style')
  el.textContent = `
    @keyframes ws-grow-kf { from { transform: scale(0.82) } to { transform: scale(1) } }
    .ws-grew .ws-grow { animation: ws-grow-kf 1.4s cubic-bezier(.2,.9,.3,1.2) both; }
    @keyframes ws-twinkle-kf { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }
    .ws-twinkle { animation: ws-twinkle-kf 1.8s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) { .ws-grew .ws-grow, .ws-twinkle { animation: none } }
  `
  document.head.appendChild(el)
}

function arrowStyle(off: boolean) {
  return {
    fontFamily: 'inherit', fontSize: 18, width: 44, height: 40, borderRadius: radius.md,
    border: `1.5px solid ${off ? theme.border : theme.green}`, background: '#FFFFFF',
    color: off ? theme.faint : theme.greenDarker, cursor: off ? 'default' : 'pointer',
  }
}

/** The stage this child last saw, so the tree can say when it has grown. */
function lastSeen(learnerId: string): number | null {
  try {
    const v = window.localStorage.getItem(`ws-tree-${learnerId}`)
    return v === null ? null : Number(v)
  } catch {
    return null
  }
}
function remember(learnerId: string, stage: number): void {
  try { window.localStorage.setItem(`ws-tree-${learnerId}`, String(stage)) } catch { /* fine */ }
}

export function Tree({
  learnerId, child, onHome,
}: { learnerId: string; child?: Child; onHome: () => void }) {
  ensureStyle()
  const [data, setData] = useState<TreeState | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [grew, setGrew] = useState(false)
  const [allStages, setAllStages] = useState(false)
  const [viewing, setViewing] = useState<number | null>(null)

  const load = () => {
    setError(null)
    api.tree(learnerId).then((t) => {
      const before = lastSeen(learnerId)
      if (before !== null && t.stage > before) {
        setGrew(true)
        window.setTimeout(() => sfx.sprout(), 300)
      }
      remember(learnerId, t.stage)
      setData(t)
    }).catch(setError)
  }
  useEffect(load, [learnerId])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data) return <Loading what="Visiting your tree" />

  // A stage already reached that the child chose to look back at, or now.
  const shown = viewing !== null && viewing < data.stage ? viewing : data.stage
  const looking = shown !== data.stage
  const toGo = data.next_at !== null ? data.next_at - data.grown : 0
  const span = data.next_at !== null ? data.next_at - data.stage_at : 1
  const part = data.next_at !== null ? (data.grown - data.stage_at) / span : 1

  return (
    <Page width={560}>
      <BackRow onBack={onHome} label="← Home" />
      <h1 style={{ margin: '4px 0 2px', fontSize: 28, color: theme.text }}>
        {data.display_name}’s tree
      </h1>
      <p style={{ margin: '0 0 12px', fontSize: 16, color: theme.muted }}>
        It grows every time one of your words is fully grown (ripe). Your field below shows them all.
      </p>

      {grew ? (
        <div role="status" style={{
          background: theme.greenPale, color: theme.greenDarker, fontWeight: 800,
          borderRadius: radius.lg, padding: '10px 14px', marginBottom: 10, textAlign: 'center',
          fontSize: 18,
        }}>
          🌱 Your tree grew! It is now {data.name.toLowerCase()}.
        </div>
      ) : null}

      <Card style={{ padding: 10, marginBottom: 14 }}>
        <div className={grew && shown === data.stage ? 'ws-grew' : undefined}>
          <TreeDrawing stage={shown} />
        </div>
        {/* Look back at any stage already reached, to see how far it has
            come (Sam, 22 Oct 2026). Stages still ahead stay a surprise. */}
        {data.stage > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
            <button type="button" aria-label="Earlier stage" disabled={shown === 0}
              onClick={() => setViewing(Math.max(0, shown - 1))} style={arrowStyle(shown === 0)}>◀</button>
            <span style={{ fontSize: 14, color: theme.muted, minWidth: 150, textAlign: 'center' }}>
              {looking
                ? <>Looking back: stage {shown + 1}</>
                : <>Tap ◀ to look back</>}
            </span>
            <button type="button" aria-label="Later stage" disabled={shown >= data.stage}
              onClick={() => setViewing(shown + 1 >= data.stage ? null : shown + 1)}
              style={arrowStyle(shown >= data.stage)}>▶</button>
          </div>
        ) : null}
        {looking ? (
          <div style={{ textAlign: 'center', marginTop: 6 }}>
            <button type="button" onClick={() => setViewing(null)} style={{
              fontFamily: 'inherit', fontSize: 15, fontWeight: 800, color: '#FFFFFF', cursor: 'pointer',
              background: theme.green, border: 'none', borderRadius: radius.pill, padding: '8px 16px',
            }}>Back to my tree now</button>
          </div>
        ) : null}
        <div style={{ textAlign: 'center', padding: '10px 6px 4px', position: 'relative' }}>
          {/* The child's own buddy, watering the tree. */}
          <div style={{ position: 'absolute', left: 4, top: -58 }} aria-hidden>
            <SproutBuddy
              buddy={{ ...(parseBuddy(child?.avatar_ref) ?? DEFAULT_BUDDY), held: 'can',
                       limbs: (parseBuddy(child?.avatar_ref) ?? DEFAULT_BUDDY).limbs === 'none' ? 'simple'
                         : (parseBuddy(child?.avatar_ref) ?? DEFAULT_BUDDY).limbs }}
              size={64} label="" />
          </div>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 26, fontWeight: 600, color: theme.greenDarker }}>
            {looking ? data.stages[shown]!.name : data.name}
          </div>
          <div style={{ fontSize: 15, color: theme.muted }}>
            {looking ? (
              <>Stage {shown + 1}, reached at {data.stages[shown]!.at.toLocaleString()} words</>
            ) : (
              <>
                Stage {data.stage + 1} of {data.stages.length} ·{' '}
                <Trace id="calc.counted from T7.grown_at">{data.grown.toLocaleString()} words grown</Trace>
              </>
            )}
          </div>
        </div>

        {data.next_at !== null && data.next_name ? (
          <div style={{ padding: '10px 8px 6px' }}>
            <div style={{
              height: 14, borderRadius: radius.pill, background: theme.sand, overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.max(4, Math.round(part * 100))}%`, height: '100%',
                background: `linear-gradient(90deg, ${theme.green}, #5FA052)`, borderRadius: radius.pill,
              }} />
            </div>
            <div style={{ fontSize: 15, color: theme.text2, marginTop: 6, textAlign: 'center' }}>
              {toGo} more {toGo === 1 ? 'word' : 'words'} to grow into <strong>{data.next_name.toLowerCase()}</strong>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 16, color: theme.greenDarker, fontWeight: 700, textAlign: 'center', padding: 8 }}>
            You grew {data.name}!
          </div>
        )}
      </Card>

      {data.fields ? <WordField fields={data.fields} /> : null}

      <Card style={{ marginBottom: 14, background: theme.orangePale }}>
        <div style={{ fontWeight: 800, color: theme.orangeDark, marginBottom: 4 }}>🌲 Did you know?</div>
        <div style={{ fontSize: 16, color: theme.text2, lineHeight: 1.5 }}>{data.fact}</div>
      </Card>

      {data.recent.length ? (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, color: theme.text, marginBottom: 8 }}>Words that grew it lately</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.recent.map((w) => (
              <span key={w} style={{
                background: theme.greenPale, color: theme.greenDarker, fontWeight: 700,
                borderRadius: radius.pill, padding: '6px 12px', fontSize: 15,
              }}><Trace id="T3.display_form">{w}</Trace></span>
            ))}
          </div>
        </Card>
      ) : null}

      <Card>
        <div style={{ fontWeight: 800, color: theme.text, marginBottom: 8 }}>From seed to forest</div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.stages.map((s, i) => {
            // Twenty-five rows is a long list on a tablet: the two before and
            // three after where the child is, unless they ask for all.
            if (!allStages && (i < data.stage - 2 || i > data.stage + 3)) return null
            const reached = i <= data.stage
            const row = {
              display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 15, width: '100%',
              padding: '6px 10px', borderRadius: radius.sm, fontFamily: 'inherit', textAlign: 'left' as const,
              background: i === data.stage ? theme.greenPale : 'transparent',
              border: `2px solid ${i === shown && looking ? theme.green : 'transparent'}`,
              color: reached ? theme.text : theme.faint, fontWeight: i === data.stage ? 800 : 500,
            }
            const inside = (
              <>
                <span>{reached ? '✓' : '○'} {s.name}</span>
                <span>{s.at === 0 ? 'start' : `${s.at.toLocaleString()} ${s.at === 1 ? 'word' : 'words'}`}</span>
              </>
            )
            return (
              <li key={s.name}>
                {reached ? (
                  <button type="button" style={{ ...row, cursor: 'pointer' }} onClick={() => {
                    setViewing(i === data.stage ? null : i)
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}>{inside}</button>
                ) : <div style={row}>{inside}</div>}
              </li>
            )
          })}
        </ol>
        <button type="button" onClick={() => setAllStages((v) => !v)} style={{
          marginTop: 8, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: theme.blue,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 10px',
        }}>
          {allStages ? 'Show fewer' : `Show all ${data.stages.length} stages`}
        </button>
      </Card>
    </Page>
  )
}
